import React, { useEffect, useRef } from 'react';
import { EventIcon, IPost, PostImage, PostLink, PostVideo, SocialEmbed } from '..';

import { IClassNames, makeClassNames } from '../../utils/styles';
import './pinned-post.scss';

import goalBackground from '../../assets/images/events/goal-background.png';
import { events, LbEvent, LbEventType } from '../../configs/events';
import { LiveblogTenant } from '../../typings/liveblog-themes';

export interface IPinnedPost extends IPost {}

const classNames = (
  event?: LbEvent,
  altBackground?: boolean,
  isWebView?: boolean,
  isGol?: boolean,
  isCheckedFlag?: boolean,
  isVideoOrImage?: boolean,
  isSkyPost?: boolean
): IClassNames =>
  makeClassNames({
    PinnedPost: 'pinned-post' /* FOR FUTURE - when and if date and time will be applied also for pinned post
    PostTimestamp: 'post__timestamp',
    PostTimestampTime: 'post__timestamp-time',
    PostTimestampDate: 'post__timestamp-date',
    */,
    PinnedPost_Wrapper: 'pinned-post__wrapper',
    PinnedPost_Icon: 'pinned-post__icon',
    PinnedPost_Container: 'pinned-post__container-v2',
    PinnedPost_Content: [
      event && !isSkyPost && 'pinned-post__event__content',
      isSkyPost && 'pinned-post__sky-post-content',
      (isGol || isCheckedFlag) && 'pinned-post__event-post-content-alt pinned-post__content-alt',
      altBackground && !event && !isWebView && 'pinned-post__content--with-alt-background',
      'pinned-post__content-v2',
    ],
    PinnedPost_Event_Container: [
      'pinned-post__event__container',
      isSkyPost && 'pinned-post__event__container--sky-post',
    ],
    PinnedPost_Event_IconTitle: [
      isGol && 'pinned-post__event__icon-title-gol',
      isCheckedFlag && 'pinned-post__event__icon-title-checked',
      event?.type === LbEventType.SPECIAL_EVENTS && 'pinned-post__event__icon-title-capitalizeFirstLetter',
      'pinned-post__event__icon-title',
    ],
    PinnedPost_ContentText: [
      (isGol || isCheckedFlag) && 'pinned-post__content-text-alt',
      'pinned-post__content-text-v2',
    ],
    PinnedPost_Title: 'pinned-post__title-v2',
    PinnedPost_Body: [
      (isGol || isCheckedFlag) && 'pinned-post__body-alt',
      (isGol || isCheckedFlag) && isVideoOrImage && 'pinned-post__body-content-margin',
      'pinned-post__body-v2',
    ],
    PinnedPost_Footer: 'pinned-post__footer',
  });

const altBackgroundStyles = {
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundImage: `url(${goalBackground})`,
  color: 'white',
};

const PinnedPost: React.FC<IPinnedPost> = ({
  postId,
  content = '',
  title,
  event,
  altBackground = false,
  video,
  image,
  social,
  acceptedCookiesConfig,
  tenant,
  isWebView,
  postLink,
}) => {
  const pinnedPostContentRef = useRef<HTMLDivElement | null>(null);
  const LMP_BORDER_COLOR = event?.borderColor || '';
  const isGol: boolean = event?.text === events?.goal.text;
  const isCheckedFlag: boolean = event?.text === events?.['bandiera a scacchi'].text;
  const isVideoOrImage: boolean = !!(video || image);
  const { socialType, src: socialSrc } = social || {};
  const isSkyPost: boolean = event?.type === LbEventType.SKY_POST;
  const isEventIconHidden: boolean = (event && event.isIconHidden) || false;

  const cn = classNames(event, altBackground, isWebView, isGol, isCheckedFlag, isVideoOrImage, isSkyPost);

  useEffect(() => {
    if (event && pinnedPostContentRef.current) {
      const isAltBackgroundPost = isGol || isCheckedFlag;
      pinnedPostContentRef.current.style.setProperty(
        '--lmpBorderColor',
        isAltBackgroundPost ? (event?.backgroundColor as string) : LMP_BORDER_COLOR
      );
      if (isAltBackgroundPost) {
        Object.assign(pinnedPostContentRef.current.style, altBackgroundStyles);
      }
    }
  }, []);

  return (
    <div className={cn.PinnedPost}>
      <div className={cn.PinnedPost_Wrapper}>
        <div className={cn.PinnedPost_Container}>
          <div ref={pinnedPostContentRef} className={cn.PinnedPost_Content}>
            {event && !isEventIconHidden && !isSkyPost && (
              <div className={cn.PinnedPost_Event_Container}>
                <EventIcon {...event} />
                <span className={cn.PinnedPost_Event_IconTitle}>{event.text}</span>
              </div>
            )}

            <div className={cn.PinnedPost_ContentText}>
              {isSkyPost && !isEventIconHidden ? (
                <div className={cn.PinnedPost_Event_Container}>
                  <EventIcon {...event} />
                  <h2 className={cn.PinnedPost_Title}>{title}</h2>
                </div>
              ) : (
                <h2 className={cn.PinnedPost_Title}>{title}</h2>
              )}

              <div className={cn.PinnedPost_Body}>
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </div>
            </div>

            {video && (
              <PostVideo
                {...video}
                containerId={`lvbg-video-player-pinned-${postId}`}
                autostart={false}
                tenant={tenant}
                isWebView={isWebView}
              />
            )}
            {image?.imgSrc && <PostImage {...image} />}
            {socialType && socialSrc && acceptedCookiesConfig && (
              <SocialEmbed
                {...social}
                src={socialSrc}
                cookieAllowed={acceptedCookiesConfig[socialType]}
                isWebView={isWebView}
              />
            )}
            {postLink?.title && (
              <PostLink
                tenant={tenant as LiveblogTenant}
                title={postLink.title}
                imageSrc={postLink.imageSrc}
                url={postLink.url}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PinnedPost;
